/**
 * Review Request Service — the cadence brain.
 *
 * Called on every completed checkout. Reads config (reviewSettingsService) + the client's
 * review state, decides whether this client is DUE for an ask, and if so queues a
 * reviewRequests doc (a dispatch function sends it when sendAfter passes). If not due, it
 * still counts the visit (for the "visits since last ask" rule). Everything configurable —
 * never bombard. Per-client state lives in clientReviewState/{clientId}.
 *
 * BEHAVIOR-PIN: SLIK review-request system — project-slik-review-request-system-2026-08-26.
 */
import {
  collection, doc, getDoc, setDoc, addDoc, updateDoc, serverTimestamp, Timestamp,
} from 'firebase/firestore';
import { db } from '../config/firebase';
import { reviewSettingsService, ReviewSettings } from './reviewSettingsService';

const REVIEW_BASE = 'https://slik-pos-public.web.app/review';

export interface ClientReviewState {
  clientId: string;
  clientName?: string;
  lastAskedAt?: any;
  lastClickedAt?: any;
  lastRating?: number;
  timesAsked?: number;
  timesClicked?: number;
  visitsSinceAsk?: number;
  optedOut?: boolean;      // "Do Not Ask" flag
  vip?: boolean;           // future per-flag overrides
}

export interface EnqueueInput {
  clientId: string;
  clientName?: string;
  clientPhone?: string;
  clientEmail?: string;
  barberId?: string;
  barberName?: string;
  appointmentId?: string;
  isNewClient?: boolean;
}

function daysBetween(a: Date, b: Date): number {
  return Math.floor((b.getTime() - a.getTime()) / 86400000);
}

async function getState(clientId: string): Promise<ClientReviewState> {
  try {
    const snap = await getDoc(doc(db, 'clientReviewState', clientId));
    if (snap.exists()) return { clientId, ...(snap.data() as any) };
  } catch { /* fall through */ }
  return { clientId };
}

/** Pure decision — is this client due for an ask right now? */
export function evaluate(
  state: ClientReviewState,
  s: ReviewSettings,
  isNewClient: boolean,
  now: Date = new Date(),
): { eligible: boolean; reason: string } {
  if (!s.enabled) return { eligible: false, reason: 'disabled' };
  if (state.optedOut) return { eligible: false, reason: 'opted_out' };

  const asked = !!state.lastAskedAt;
  if (!asked) {
    if (isNewClient) {
      return s.newClientFirstVisit
        ? { eligible: true, reason: 'new_client_first_visit' }
        : { eligible: false, reason: 'new_client_wait' };
    }
    return { eligible: true, reason: 'first_ask' };
  }

  // Returning client: the shop's chosen cadence decides "due" (days-clock OR visit-count).
  const lastAsked: Date = state.lastAskedAt?.toDate ? state.lastAskedAt.toDate() : new Date(state.lastAskedAt);
  const due = s.useVisitsSinceAsk
    ? (state.visitsSinceAsk ?? 0) >= s.visitsSinceAsk
    : daysBetween(lastAsked, now) >= s.minDaysBetweenAsks;

  return due ? { eligible: true, reason: 'due' } : { eligible: false, reason: 'suppressed' };
}

/**
 * Called on a completed checkout. Queues an ask if due; else just counts the visit.
 * Non-throwing by contract — the caller wraps it, but we also swallow internally so a
 * review hiccup can never affect checkout.
 */
export async function enqueueForCheckout(
  input: EnqueueInput,
  settings?: ReviewSettings,
): Promise<{ queued: boolean; reason: string; requestId?: string; link?: string }> {
  try {
    if (!input.clientId) return { queued: false, reason: 'no_client' };
    const s = settings || (await reviewSettingsService.get());
    const state = await getState(input.clientId);
    const { eligible, reason } = evaluate(state, s, !!input.isNewClient);

    if (!eligible) {
      // Not asking, but the visit still counts toward "visits since last ask".
      await setDoc(doc(db, 'clientReviewState', input.clientId), {
        clientId: input.clientId,
        visitsSinceAsk: (state.visitsSinceAsk ?? 0) + 1,
      }, { merge: true });
      return { queued: false, reason };
    }

    const sendAfter = Timestamp.fromMillis(Date.now() + Math.max(0, s.delayMinutes) * 60000);
    const ref = await addDoc(collection(db, 'reviewRequests'), {
      clientId: input.clientId,
      clientName: input.clientName || state.clientName || '',
      clientPhone: input.clientPhone || '',
      clientEmail: input.clientEmail || '',
      barberId: input.barberId || '',
      barberName: input.barberName || '',
      appointmentId: input.appointmentId || '',
      channel: s.channel,
      status: 'queued',
      sendAfter,
      createdAt: serverTimestamp(),
    });
    const link = `${REVIEW_BASE}/${ref.id}`;
    await updateDoc(ref, { reviewLink: link });

    // Reset the client's cadence counters — we just asked.
    await setDoc(doc(db, 'clientReviewState', input.clientId), {
      clientId: input.clientId,
      clientName: input.clientName || state.clientName || '',
      lastAskedAt: serverTimestamp(),
      timesAsked: (state.timesAsked ?? 0) + 1,
      visitsSinceAsk: 0,
    }, { merge: true });

    return { queued: true, reason, requestId: ref.id, link };
  } catch (e) {
    console.warn('enqueueForCheckout failed (non-blocking):', e);
    return { queued: false, reason: 'error' };
  }
}
